import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../context/AuthContext';
import { useClientProfile } from './ClientProfileContext';

const AmazonBrandRegistryContext = createContext();

export const useAmazonRegistry = () => {
  const context = useContext(AmazonBrandRegistryContext);
  if (!context) throw new Error('useAmazonRegistry must be used within AmazonBrandRegistryProvider');
  return context;
};

export const AmazonBrandRegistryProvider = ({ children }) => {
  const { isVendr } = useAuth();
  const { profile, loading: profileLoading } = useClientProfile();
  const [registry, setRegistry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isAmazonEnabled = !!profile?.amazon_enabled;
  const isAmazonSubmitted = registry?.status === 'submitted';

  const loadRegistry = useCallback(async () => {
    if (!profile?.id || !isVendr) {
      setRegistry(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('amazon_brand_registry')
        .select('*')
        .eq('client_profile_id', profile.id)
        .maybeSingle();
      if (fetchError) throw fetchError;
      setRegistry(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [profile?.id, isVendr]);

  useEffect(() => {
    if (!profileLoading) loadRegistry();
  }, [profileLoading, loadRegistry]);

  const saveRegistry = async (values, shouldSubmit = false) => {
    if (!profile?.id) return { success: false, error: 'No profile' };
    const payload = {
      ...values,
      status: shouldSubmit ? 'submitted' : 'draft',
      submitted_at: shouldSubmit ? new Date().toISOString() : (registry?.submitted_at ?? null),
      updated_at: new Date().toISOString(),
    };
    try {
      if (registry?.id) {
        const { data, error: updateError } = await supabase
          .from('amazon_brand_registry')
          .update(payload)
          .eq('id', registry.id)
          .select()
          .single();
        if (updateError) return { success: false, error: updateError.message };
        setRegistry(data);
        return { success: true, data };
      }
      const { data, error: insertError } = await supabase
        .from('amazon_brand_registry')
        .insert([{ ...payload, client_profile_id: profile.id }])
        .select()
        .single();
      if (insertError) return { success: false, error: insertError.message };
      setRegistry(data);
      return { success: true, data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  return (
    <AmazonBrandRegistryContext.Provider
      value={{ registry, loading, error, isAmazonEnabled, isAmazonSubmitted, loadRegistry, saveRegistry }}
    >
      {children}
    </AmazonBrandRegistryContext.Provider>
  );
};
